import { Check, FileText, Plus, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "@/app/context/Auth/AuthContext";
import { useFitCheckBasket } from "@/app/context/FitCheckBasket";

import Loader from "../../../components/Loader";
import { Button } from "../../../components/ui/button";
import { buildDocumentUrl } from "../../../handlers/documentStorage";
import { useSmartProfile } from "../../../services/create-resume/queries";
import { useCreateFitCheckMutation } from "../config/api";
import BulkFitCheckLoading from "./BulkFitCheckLoading";
import DSPItem from "./DSPItem";
import JobItem from "./JobItem";
import ProgressDots from "./ProgressDots";

type BulkFitCheckStep = "SELECT_JOBS" | "SELECT_DSP" | "CONFIRM" | "LOADING" | "COMPLETE";

interface DSP {
	dsp_id: string;
	dsp_title: string;
	dsp_description: string;
	source_resume: {
		doc_id: string;
		file_name: string;
		storage_key: string;
	};
}

interface BulkFitCheckFlowProps {
	isOpen: boolean;
	onClose: () => void;
}

function BulkFitCheckFlow({ isOpen, onClose }: BulkFitCheckFlowProps) {
	const navigate = useNavigate();
	const { userData } = useAuth();
	const { basketItems, removeFromBasket } = useFitCheckBasket();
	const { data: smartProfileData, isLoading: isLoadingDSP } = useSmartProfile();
	const { mutateAsync: createFitCheck } = useCreateFitCheckMutation();

	const [currentStep, setCurrentStep] = useState<BulkFitCheckStep>("SELECT_JOBS");
	const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set(basketItems.map((item) => item.jobId)));
	const [selectedDSP, setSelectedDSP] = useState<DSP | null>(null);
	const [processingProgress, setProcessingProgress] = useState(0);
	const [currentProcessingJob, setCurrentProcessingJob] = useState<string>("");
	const [processedCount, setProcessedCount] = useState(0);
	const [processedJobIds, setProcessedJobIds] = useState<string[]>([]);

	const dsps: DSP[] = smartProfileData?.dsps || [];
	const selectedJobs = basketItems.filter((item) => selectedJobIds.has(item.jobId));

	const toggleJobSelection = (jobId: string) => {
		setSelectedJobIds((prev) => {
			const next = new Set(prev);
			if (next.has(jobId)) {
				next.delete(jobId);
			} else {
				next.add(jobId);
			}
			return next;
		});
	};

	const handleContinueToSelectDSP = () => {
		if (selectedJobIds.size === 0) {
			toast.error("Please select at least one job");
			return;
		}
		setCurrentStep("SELECT_DSP");
	};

	const handleContinueToConfirm = () => {
		if (!selectedDSP) {
			toast.error("Please select a resume");
			return;
		}
		setCurrentStep("CONFIRM");
	};

	const handleRunFitChecks = async () => {
		if (!userData || !selectedDSP) return;

		setCurrentStep("LOADING");
		const resumeURL = buildDocumentUrl(selectedDSP.source_resume.storage_key);
		const jobsToProcess = selectedJobs.map((item) => item.jobDetails);
		const totalJobs = jobsToProcess.length;

		const user = {
			firstName: userData.firstName,
			lastName: userData.lastName,
			email: userData.email,
			userId: userData.userID,
		};

		let completedCount = 0;

		for (const job of jobsToProcess) {
			setCurrentProcessingJob(job.jobTitle);
			try {
				await createFitCheck({
					userId: userData.userID,
					jobId: job._id,
					resumeURL,
					user,
					jobDetails: job as unknown as Record<string, unknown>,
				});
				completedCount++;
				setProcessingProgress(Math.round((completedCount / totalJobs) * 100));
				setProcessedJobIds((prev) => [...prev, job._id]);
			} catch (error) {
				console.error(`Failed to process FitCheck for job ${job._id}:`, error);
				// Continue with other jobs even if one fails
			}
		}

		setProcessedCount(completedCount);
		setCurrentStep("COMPLETE");
	};

	const handleAddAnotherJob = () => {
		// Go back to step 1 and close the drawer to let user browse more jobs
		setCurrentStep("SELECT_JOBS");
		onClose();
	};

	const handleRemoveFromBasket = (jobId: string) => {
		removeFromBasket(jobId);
		setSelectedJobIds((prev) => {
			const next = new Set(prev);
			next.delete(jobId);
			return next;
		});
	};

	const resetState = () => {
		setCurrentStep("SELECT_JOBS");
		setSelectedDSP(null);
		setProcessingProgress(0);
		setCurrentProcessingJob("");
		setProcessedCount(0);
		setProcessedJobIds([]);
	};

	const handleClose = () => {
		// Remove processed jobs from basket
		processedJobIds.forEach((jobId) => removeFromBasket(jobId));
		resetState();
		onClose();
	};

	const handleDone = () => {
		// Remove all processed jobs from basket and reset
		processedJobIds.forEach((jobId) => removeFromBasket(jobId));
		resetState();
		onClose();
		// Redirect to FitCheck dashboard
		navigate("/fitchecks");
	};

	const handleCreateNewDSP = () => {
		navigate("/profile");
		onClose();
	};

	const _handleBack = () => {
		if (currentStep === "SELECT_DSP") {
			setCurrentStep("SELECT_JOBS");
		} else if (currentStep === "CONFIRM") {
			setCurrentStep("SELECT_DSP");
		}
	};

	const handleChangeDSP = () => {
		setCurrentStep("SELECT_DSP");
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-hidden">
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

			{/* Drawer - slides from right */}
			<div className="absolute inset-y-0 right-0 flex max-w-full">
				<div className="relative w-screen max-w-xl bg-white shadow-xl flex flex-col">
					{/* Header */}
					<div className="flex items-center justify-between px-6 py-4">
						<ProgressDots currentStep={currentStep} />
						<button
							onClick={handleClose}
							className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
						>
							<X className="h-5 w-5" />
						</button>
					</div>

					{/* Content */}
					<div className="flex-1 overflow-y-auto">
						{/* Step 1: Select Jobs */}
						{currentStep === "SELECT_JOBS" && (
							<div>
								<div className="px-6 border-b">
									<h2 className="text-xl font-bold text-gray-900 mb-1">Your FitCheck Basket</h2>
									<p className="text-gray-600 text-sm mb-4">
										Review your selected jobs or add a new one to see where you fit best before applying.
									</p>

									<div className="flex items-center justify-between mb-3">
										<p className="text-sm font-medium text-gray-900">
											You have {basketItems.length} job{basketItems.length !== 1 ? "s" : ""} to FitCheck
										</p>
									</div>
								</div>

								<div className="mt-4 px-6">
									<button
										onClick={handleAddAnotherJob}
										className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 text-sm font-medium mb-4"
									>
										<Plus className="w-4 h-4" />
										Add Another Job
									</button>

									<div className="space-y-4">
										{basketItems.map((item) => (
											<JobItem
												key={item.jobId}
												job={item.jobDetails}
												isSelected={selectedJobIds.has(item.jobId)}
												onToggle={() => toggleJobSelection(item.jobId)}
												onRemove={() => handleRemoveFromBasket(item.jobId)}
											/>
										))}
									</div>
								</div>
							</div>
						)}

						{/* Step 2: Select DSP */}
						{currentStep === "SELECT_DSP" && (
							<div>
								<div className="px-6 border-b">
									<h2 className="text-xl font-bold text-gray-900 mb-1">Your FitCheck Basket</h2>
									<p className="text-gray-600 text-sm mb-6">
										Review your selected jobs or add a new one to see where you fit best before applying.
									</p>
								</div>

								{/* Jobs summary */}
								<div className="mt-4 px-6">
									<div className="flex items-center justify-between mb-3">
										<p className="text-sm font-medium text-gray-900">Jobs to FitCheck</p>
										<button
											onClick={handleAddAnotherJob}
											className="text-blue-600 hover:text-blue-700 text-sm font-medium"
										>
											Add a job
										</button>
									</div>
									<div className="space-y-2">
										{selectedJobs.map((item) => (
											<JobItem key={item.jobId} job={item.jobDetails} isSelected={true} showCheckbox={true} />
										))}
									</div>

									{/* DSP Selection */}
									<div className="mt-6">
										<p className="text-sm font-medium text-gray-900 mb-1">Choose a resume to compare</p>
										<p className="text-gray-500 text-sm mb-4">
											Select the Smart Profile that best matches the roles you&apos;ve added.
										</p>

										{isLoadingDSP ? (
											<div className="flex items-center justify-center py-8">
												<Loader />
											</div>
										) : dsps.length === 0 ? (
											<div className="bg-gray-50 rounded-lg p-6 text-center">
												<FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
												<p className="text-sm text-gray-600 mb-4">
													You don&apos;t have a DSP yet. Create one to run FitCheck.
												</p>
												<Button onClick={handleCreateNewDSP} size="sm">
													Create DSP
												</Button>
											</div>
										) : (
											<div className="space-y-2 mb-4">
												{dsps.map((dsp) => (
													<DSPItem
														key={dsp.dsp_id}
														dsp={dsp}
														isSelected={selectedDSP?.dsp_id === dsp.dsp_id}
														onSelect={() => setSelectedDSP(dsp)}
													/>
												))}
											</div>
										)}

										<button
											onClick={handleCreateNewDSP}
											className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 text-sm font-medium"
										>
											<Plus className="w-4 h-4" />
											Create New DSP
										</button>
									</div>
								</div>
							</div>
						)}

						{/* Step 3: Confirm */}
						{currentStep === "CONFIRM" && (
							<div>
								<div className="px-6 mb-4 border-b">
									<h2 className="text-xl font-bold text-gray-900 mb-1">
										Ready to Run Your {selectedJobs.length} FitCheck{selectedJobs.length !== 1 ? "s" : ""}?
									</h2>
									<p className="text-gray-600 text-sm mb-6">
										We&apos;ll compare your profile to the jobs you selected and show your strengths, gaps, and focus
										areas.
									</p>
								</div>

								{/* Jobs summary */}
								<div className="px-6">
									<div className="flex items-center justify-between mb-3">
										<p className="text-sm font-medium text-gray-900">Jobs to FitCheck</p>
										<button
											onClick={handleAddAnotherJob}
											className="text-blue-600 hover:text-blue-700 text-sm font-medium"
										>
											Add a job
										</button>
									</div>
									<div className="space-y-2">
										{selectedJobs.map((item) => (
											<JobItem key={item.jobId} job={item.jobDetails} isSelected={true} showCheckbox={true} />
										))}
									</div>

									{/* Selected DSP */}
									<div className="mt-6">
										<div className="flex items-center justify-between mb-3">
											<p className="text-sm font-medium text-gray-900">Resume to compare</p>
											<button
												onClick={handleChangeDSP}
												className="text-blue-600 hover:text-blue-700 text-sm font-medium"
											>
												Change
											</button>
										</div>
										{selectedDSP && (
											<div className="flex items-start gap-3 p-4 rounded-lg border border-blue-600 bg-white">
												<div className="flex-1 min-w-0">
													<p className="font-bold text-base text-gray-900">{selectedDSP.dsp_title}</p>
													<p className="text-sm text-gray-700 font-medium mt-0.5">{selectedDSP.dsp_description}</p>
													<div className="flex items-center gap-1.5 text-xs text-gray-500">
														<FileText className="w-3 h-3" />
														<span>Resume: {selectedDSP.source_resume.file_name}</span>
													</div>
												</div>
												<div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
													<Check className="w-3 h-3 text-white" strokeWidth={3} />
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
						)}

						{/* Step 4: Loading */}
						{currentStep === "LOADING" && (
							<div className="px-6">
								<BulkFitCheckLoading
									totalJobs={selectedJobs.length}
									progress={processingProgress}
									currentJob={currentProcessingJob}
									dspTitle={selectedDSP?.dsp_title || ""}
								/>
							</div>
						)}

						{/* Step 5: Complete - Background Processing Message */}
						{currentStep === "COMPLETE" && (
							<div className="text-center py-8 px-6">
								<div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
									<Sparkles className="w-8 h-8 text-blue-600" />
								</div>
								<h2 className="text-xl font-bold text-gray-900 mb-2">FitCheck Started!</h2>
								<p className="text-gray-600 mb-2">
									We&apos;re analyzing your fit for {processedCount} job{processedCount !== 1 ? "s" : ""} in the
									background.
								</p>
								<p className="text-gray-500 text-sm mb-6">
									You&apos;ll be able to view your results in your FitCheck dashboard once they&apos;re ready.
								</p>
								<Button onClick={handleDone} className="w-full">
									Done
								</Button>
							</div>
						)}
					</div>

					{/* Footer */}
					{currentStep !== "LOADING" && currentStep !== "COMPLETE" && (
						<div className="px-6 py-4 border-t">
							{currentStep === "SELECT_JOBS" && (
								<Button onClick={handleContinueToSelectDSP} disabled={selectedJobIds.size === 0} className="w-full">
									Next: Select Resume
								</Button>
							)}
							{currentStep === "SELECT_DSP" && (
								<Button onClick={handleContinueToConfirm} disabled={!selectedDSP} className="w-full">
									Run FitCheck
								</Button>
							)}
							{currentStep === "CONFIRM" && (
								<Button onClick={handleRunFitChecks} className="w-full">
									Run FitCheck
								</Button>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default BulkFitCheckFlow;
